import {
	campaignsShowQueryKey,
	InternalFormData,
	ModulesFormDataInputQuestionData,
	useCampaignsShow,
	useFormsUpdate,
} from '@jobilla/nexus-sdk'
import { useAtomValue } from 'jotai'
import { isNil } from 'lodash'
import { v4 } from 'uuid'
import { queryClient } from '../../main'
import { activeFormChatToolAtom, CampaignForm, CampaignScoreValues, logger, useFormEditor } from '../../ui'

type UseFormEditorOptions = {
	postLaunch?: boolean
}

export function useNewFormEditor(campaignId: string, options?: UseFormEditorOptions) {
	const postLaunch = options?.postLaunch ?? false
	const campaign = useCampaignsShow(campaignId)

	const formData = campaign.data?.form
	const form = mapFormToCampaignForm(formData)

	const formMutation = useFormsUpdate({
		mutationKey: ['update-form'],
		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
	})

	const handleFormChange = async (updatedForm: CampaignForm) => {
		if (!formData) {
			logger.error('Form not found during edit', { data: { campaignId, postLaunch } })
			throw new Error('Form not found')
		}

		// Validate contact consent questions for duplicate consent types
		const hasInvalidConsent = updatedForm.questions.some((question) => {
			if (question.attribute !== 'contact_consent' || !question.options) {
				return false
			}

			const consentTypes = question.options
				.map((opt) => opt.consentType)
				.filter(Boolean)

			const uniqueConsentTypes = new Set(consentTypes)
			return consentTypes.length !== uniqueConsentTypes.size
		})

		if (hasInvalidConsent) {
			logger.warn('Cannot save: Duplicate consent types found in contact consent question', {
				category: 'validation',
				data: { campaignId },
			})
			return
		}

		// Validate contact consent questions have required consent types (positive and negative)
		const missingRequiredConsentTypes = updatedForm.questions.some((question) => {
			if (question.attribute !== 'contact_consent' || !question.options) {
				return false
			}

			const consentTypes = question.options
				.map((opt) => opt.consentType)
				.filter(Boolean)

			const hasPositive = consentTypes.includes('positive')
			const hasNegative = consentTypes.includes('negative')

			return !hasPositive || !hasNegative
		})

		if (missingRequiredConsentTypes) {
			logger.warn('Cannot save: Consent question needs both a "Yes" option and a "No" option', {
				category: 'validation',
				data: { campaignId },
			})
			return
		}

		const hasEmptyLabel = updatedForm.questions.some((question) => !question.label?.trim())
		if (hasEmptyLabel) {
			return
		}

		const questions = mapCampaignFormToCreateFormData(updatedForm)

		await formMutation.mutateAsync({
			form: formData.id ?? '',
			data: {
				language: formData.language ?? 'en',
				questions,
			},
		})
	}

	const isToolExecuting = useAtomValue(activeFormChatToolAtom)
	const isGenerating = postLaunch ? false : formData?.isGenerated !== true

	useFormEditor({
		form: form.questions.length > 0 ? form : undefined,
		isPending: campaign.isPending,
		isSaving: formMutation.isPending,
		isGenerating,
		isToolExecuting: postLaunch ? false : isToolExecuting,
		onFormChange: handleFormChange,
		formVersion: formData?.updatedAt,
	})

	return {
		isPending: campaign.isPending,
		isSaving: formMutation.isPending,
		isGenerating,
	}
}

export function mapFormToCampaignForm(data: InternalFormData | undefined | null): CampaignForm {
	if (!data) {
		return {
			id: '',
			questions: [],
		}
	}
	return {
		...data,
		questions: data?.questions?.length
			? data?.questions.map((question) => {
				const mappedQuestion = {
					id: question.id ?? '',
					label: question.label ?? '',
					attribute: question.attribute ?? null,
					subtype: question.subtype ?? null,
					type: question.type,
					weight: question.scoreWeight ?? 1,
					validationRules: question.validationRules ?? null,
				}

				if (question.options) {
					return {
						...mappedQuestion,
						options: question.options.map((option) => ({
							id: option.id,
							label: option.label,
							isDisqualifying: option.isDisqualifying ?? false,
							consentType: option.consentType ?? null,
						})),
						scores: question.options.map((option) => ({
							questionId: question.id ?? '',
							optionId: option.id,
							scoreValue: (!isNil(option.scoreValue)
								? `${option.scoreValue}`
								: CampaignScoreValues.EXCELLENT.toString()) as CampaignScoreValues,
						})),
					}
				}

				return mappedQuestion
			})
			: [],
	} satisfies CampaignForm
}

export function mapCampaignFormToCreateFormData(form: CampaignForm) {
	if (!form) {
		return []
	}
	return form.questions.map((question, index) => {
		return {
			id: question.id ?? '',
			label: question.label ?? '',
			type: question.type,
			attribute: question?.attribute ?? null,
			subtype: question.subtype ?? null,
			validationRules: question.validationRules ?? null,
			position: index,
			weight: question.weight ?? 1,
			scores: question.scores?.map((score) => ({
				...score,
				scoreValue: !isNil(score.scoreValue)
					? parseInt(score.scoreValue)
					: parseInt(CampaignScoreValues.EXCELLENT),
			})) ?? [],
			options: question.options
				? question.options.map((option, index) => ({
					...option,
					// @TODO This is an actual crime from the backend.
					id: option.id.length !== 36 ? v4() : option.id,
					label: option.label,
					position: index,
					isDisqualifying: option.isDisqualifying ?? false,
					scoreValue: null, // Score is handled separately in the scores array
					consentType: option.consentType ?? null,
				}))
				: [],
		} satisfies ModulesFormDataInputQuestionData
	})
}
